import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material'
import { authApi } from '@/api/auth'
import { rememberManualLogout } from '@/api/authDiagnostics'
import { AuthLogo } from '@/components/auth/AuthLogo'
import {
  CabinetActivationForm,
  type CabinetActivationUnavailable,
} from '@/components/auth/CabinetActivationForm'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { detachWebPushBeforeLogout } from '@/hooks/useWebPush'
import { useAuthStore } from '@/stores/authStore'
import type { CabinetInvitationPreview } from '@/types'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { getUserDisplayName } from '@/utils/userName'
import '@/styles/auth.css'

/** Параметр адреса с кодом: API собирает ссылку как `/cabinet/activate?code=…` */
const CODE_PARAM = 'code'

type PageState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  /** Не удалось проверить ссылку (сеть, лимит запросов) — можно повторить */
  | { kind: 'error'; message: string | null }
  | { kind: 'ready'; preview: CabinetInvitationPreview }

/**
 * Публичная страница активации кабинета: ученик, которого учитель вёл без
 * кабинета, задаёт пароль по ссылке из приглашения и входит в кабинет.
 *
 * Разметка — как у остальных страниц входа (`ResetPasswordPage`). Подсказки
 * страницы (`PageHelpPopover`) здесь нет, как и на других страницах входа.
 */
export function CabinetActivatePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const code = searchParams.get(CODE_PARAM)?.trim() ?? ''
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const authUser = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const [state, setState] = useState<PageState>(code === '' ? { kind: 'invalid' } : { kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [loggingOut, setLoggingOut] = useState(false)

  useEffect(() => {
    if (code === '') {
      setState({ kind: 'invalid' })
      return
    }

    let active = true
    setState({ kind: 'loading' })
    authApi
      .getCabinetInvitation(code)
      .then((preview) => {
        if (active) setState({ kind: 'ready', preview })
      })
      .catch((err: unknown) => {
        if (!active) return
        const status = (err as { response?: { status?: number } }).response?.status
        setState(status === 404 ? { kind: 'invalid' } : { kind: 'error', message: getErrorMessage(err) })
      })

    return () => {
      active = false
    }
  }, [code, attempt])

  // Форма узнала при отправке, что ссылка уже не работает
  const handleUnavailable = (reason: CabinetActivationUnavailable) => {
    setState((current) => {
      if (reason === 'invalid' || current.kind !== 'ready') {
        return { kind: 'invalid' }
      }
      return { kind: 'ready', preview: { ...current.preview, status: reason } }
    })
  }

  // Активировать чужой кабинет из-под своей сессии нельзя — сначала выход
  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await detachWebPushBeforeLogout()
      await authApi.logout()
    } catch {
      // Сессия могла уже закончиться — выходим локально
    } finally {
      rememberManualLogout()
      logout()
      setLoggingOut(false)
    }
  }

  const renderContent = () => {
    if (state.kind === 'loading') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Skeleton variant="text" width="70%" sx={{ mx: 'auto' }} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={48} />
        </Box>
      )
    }

    if (state.kind === 'invalid') {
      return (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {t('cabinetActivation.invalid')}
        </Alert>
      )
    }

    if (state.kind === 'error') {
      return (
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setAttempt((value) => value + 1)}>
              {t('common.retry')}
            </Button>
          }
        >
          {displayErrorMessage(state.message, t('cabinetActivation.loadError'))}
        </Alert>
      )
    }

    const { preview } = state

    if (preview.status === 'expired') {
      return (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t('cabinetActivation.expired')}
        </Alert>
      )
    }

    if (preview.status === 'used') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t('cabinetActivation.used', { email: preview.email ?? '' })}
          </Alert>
          <Button component={Link} to={ROUTES.LOGIN} variant="contained" size="large" fullWidth>
            {t('auth.login')}
          </Button>
        </Box>
      )
    }

    return (
      <>
        <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 600, mb: 1 }}>
          {preview.teacher_name
            ? t('cabinetActivation.subtitleTeacher', { teacher: preview.teacher_name })
            : t('cabinetActivation.subtitleGeneric')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
          {t('cabinetActivation.description')}
        </Typography>

        {preview.student_name && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('cabinetActivation.student')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {preview.student_name}
            </Typography>
          </Box>
        )}

        {isAuthenticated ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {t('cabinetActivation.loggedIn', {
                name: getUserDisplayName(authUser, { prefer: 'full_name' }) || t('dashboard.user'),
              })}
            </Alert>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={loggingOut}
              onClick={() => void handleLogout()}
            >
              {loggingOut ? t('common.loading') : t('auth.logout')}
            </Button>
          </Box>
        ) : (
          <CabinetActivationForm
            code={code}
            email={preview.email ?? ''}
            onUnavailable={handleUnavailable}
          />
        )}
      </>
    )
  }

  return (
    <Box className="auth-page">
      <Box className="auth-language-switcher">
        <LanguageSwitcher />
      </Box>
      <Box className="auth-container">
        <Card className="auth-card">
          <CardContent>
            <AuthLogo />

            <Typography variant="h1" component="h1" sx={{ textAlign: 'center', mb: 2 }}>
              {t('cabinetActivation.title')}
            </Typography>

            {renderContent()}

            {/* У использованной ссылки кнопка «Войти» уже есть — ссылку внизу не дублируем */}
            {!(state.kind === 'ready' && state.preview.status === 'used') && (
              <Box className="auth-footer">
                <Typography variant="body2">
                  {t('auth.haveAccount')}{' '}
                  <Link to={ROUTES.LOGIN} className="auth-link">
                    {t('auth.login')}
                  </Link>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
